// Data Lab v2 · Calls — AI Tag KPIs + category/sentiment/priority facets + transcript preview.
// Every synced JustCall record surfaces its Talmia AI tagging (category, sentiment,
// priority, score band) and — once live — its transcript from JCCall.data.transcript.
import { useEffect, useMemo, useState } from "react";
import Layout from "../../../components/Layout";
import api from "../../../lib/api";
import LabListTable from "../LabListTable";
import { dateGB, Chip, asText } from "../_shared";
import { KpiBand, KpiTile, FacetChips, Histogram, V2Banner, disableV2 } from "./_shared";
import { T2Panel } from "../../quotes/_t2";
import { PhoneCall, MessageSquare, Star, TrendingUp, Bot, ClipboardList, FileAudio } from "lucide-react";

const SENTIMENT_TONE = (v) => {
  const s = String(v || "").toLowerCase();
  if (s.includes("positive")) return "green";
  if (s.includes("negative")) return "red";
  if (s.includes("neutral"))  return "slate";
  return "slate";
};
const PRIORITY_TONE = (v) => {
  const s = String(v || "").toLowerCase();
  if (s.includes("high"))   return "red";
  if (s.includes("medium")) return "warn";
  if (s.includes("low"))    return "slate";
  return "slate";
};

const COLUMNS = [
  { key: "created",  label: "When",        path: "CreatedDate",                             sortable: true,  w: "w-40", fmt: dateGB, className: "font-mono text-slate-300 text-xs" },
  { key: "agent",    label: "Agent",       path: "JCCall.data.agent_name",                  w: "w-40", className: "text-white" },
  { key: "contact",  label: "Contact",     path: "JCCall.data.contact_name",                w: "w-40" },
  { key: "number",   label: "Number",      path: "JCCall.data.contact_number",              w: "w-36", className: "font-mono text-slate-400" },
  { key: "category", label: "Category",    path: "AITags.category",                         sortable: true, w: "w-28",
    fmt: (v) => v ? <Chip tone="teal">{v}</Chip> : <span className="text-slate-500">—</span> },
  { key: "sent",     label: "Sentiment",   path: "AITags.sentiment",                        sortable: true, w: "w-28",
    fmt: (v) => v ? <Chip tone={SENTIMENT_TONE(v)}>{v}</Chip> : <span className="text-slate-500">—</span> },
  { key: "priority", label: "Priority",    path: "AITags.priority",                         sortable: true, w: "w-24",
    fmt: (v) => v ? <Chip tone={PRIORITY_TONE(v)}>{v}</Chip> : <span className="text-slate-500">—</span> },
  { key: "score",    label: "Score",       path: "AITags.score.overall",                    sortable: true, w: "w-20",
    fmt: (v) => v == null ? "—" : <span className="font-mono text-white font-bold">{v}</span> },
  { key: "band",     label: "Band",        path: "AITags.score.band",                       w: "w-24",
    fmt: (v) => v ? <Chip tone={String(v).toLowerCase() === "excellent" ? "green" : String(v).toLowerCase() === "poor" ? "red" : "slate"}>{v}</Chip> : "—" },
];

export default function LabCallsV2() {
  const [insights, setInsights] = useState(null);
  const [cats, setCats] = useState([]);
  const [sents, setSents] = useState([]);
  const [prios, setPrios] = useState([]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/lab/calls/insights"); setInsights(data); }
      catch { setInsights(null); }
    })();
  }, []);

  const searchAppend = useMemo(() => [...cats, ...sents, ...prios].join(" "), [cats, sents, prios]);

  return (
    <Layout>
      <div data-testid="lab-calls-v2">
        <V2Banner onDisable={disableV2} />
        <div style={{ marginBottom: 14 }}>
          <div className="t2-page-eyebrow">
            <PhoneCall className="w-3 h-3" /> Data Lab · Calls
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: "var(--t2-white)", margin: "4px 0 3px" }}>Calls</h1>
          <p className="t2-mono" style={{ fontSize: 11.5, color: "var(--t2-grey3)" }}>
            JustCall records enriched with Talmia AI tagging (Gemini 3 Flash) — category, sentiment, priority and coaching score.
          </p>
        </div>

        <KpiBand testid="calls-kpis">
          <KpiTile label="Calls"          value={insights?.total ?? "…"}                              icon={PhoneCall}    testid="kpi-calls-total" />
          <KpiTile label="AI tagged"      value={insights?.tagged ?? "…"}                             icon={Bot}          tone="teal"   testid="kpi-calls-tagged" />
          <KpiTile label="Transcripts"    value={insights?.with_transcript ?? "…"}                    icon={FileAudio}    tone="brass"  testid="kpi-calls-transcripts" sub="wire when live" />
          <KpiTile label="Avg score"      value={insights?.avg_score ?? "…"}                          icon={Star}         tone="orange" testid="kpi-calls-avg" sub={`across ${insights?.scored_count ?? 0} scored`} />
          <KpiTile label="Top category"   value={asText(insights?.categories?.[0]?.category || "—")}  icon={MessageSquare} tone="teal"  testid="kpi-calls-top-cat" />
          <KpiTile label="Top sentiment"  value={asText(insights?.sentiments?.[0]?.sentiment || "—")} icon={TrendingUp}   tone="warn"   testid="kpi-calls-top-sent" />
        </KpiBand>

        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
          <T2Panel icon={Star} title="Score bands" right={<span className="t2-mono">{insights?.scored_count ?? 0} scored</span>} testid="calls-score-panel">
            {insights?.score_bins?.length ? (
              <Histogram bins={insights.score_bins} tone="orange" height={130} testid="calls-score-histogram" />
            ) : (
              <div className="t2-mono" style={{ fontSize: 11, color: "var(--t2-grey4)" }}>No scored calls yet.</div>
            )}
          </T2Panel>
          <T2Panel icon={MessageSquare} title="Filter by category" testid="calls-facet-cat">
            <FacetChips
              testid="calls-facet-cat-chips"
              options={(insights?.categories || []).map((s) => ({ value: s.category, count: s.count }))}
              selected={cats}
              onChange={setCats}
            />
          </T2Panel>
          <T2Panel icon={TrendingUp} title="Sentiment / Priority" testid="calls-facet-mood">
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <FacetChips
                testid="calls-facet-sent-chips"
                options={(insights?.sentiments || []).map((s) => ({ value: s.sentiment, count: s.count }))}
                selected={sents}
                onChange={setSents}
              />
              <FacetChips
                testid="calls-facet-prio-chips"
                options={(insights?.priorities || []).map((s) => ({ value: s.priority, count: s.count }))}
                selected={prios}
                onChange={setPrios}
              />
            </div>
          </T2Panel>
        </div>

        <LabListTable
          collection="calls"
          columns={COLUMNS}
          defaultSort="CreatedDate"
          detailPath={(id) => `/lab/calls/${id}`}
          searchPlaceholder="Search agent, contact, number, category, sentiment, action items…"
          testidPrefix="lab-call"
          extraSearch={searchAppend}
        />
      </div>
    </Layout>
  );
}
